import React, { useRef, useState } from 'react';
import axios from 'axios';
import { FileJson, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const API = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

/**
 * NicheFinderUploadModal — modal de import JSON.
 *
 * Replica o UploadModal inline do legado (NichosViraisV2Page), sem
 * dependencia daquele arquivo. POST multipart pra /nichos-virais/import +
 * polling em /import/status ate concluir.
 */
const NicheFinderUploadModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error(t('viralNiches.select_json_file'));
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/nichos-virais/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (data.status === 'already_running') {
        toast.info(t('viralNiches.processing'));
      } else {
        toast.success(t('viralNiches.processing_channels_api'));
      }

      // Polling — mesma estrategia do legado
      const interval = setInterval(async () => {
        try {
          const { data: status } = await axios.get(`${API}/nichos-virais/import/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!status.running) {
            clearInterval(interval);
            setUploading(false);
            if ((status.inserted ?? 0) + (status.updated ?? 0) + (status.errors ?? 0) > 0) {
              toast.success(
                `${status.inserted ?? 0} ✓ · ${status.updated ?? 0} ↻ · ${status.errors ?? 0} ✗`,
                { duration: 5000 },
              );
            }
            onSuccess && onSuccess();
          }
        } catch (e) {
          clearInterval(interval);
          setUploading(false);
        }
      }, 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('viralNiches.error_import_file'));
      setUploading(false);
    }
  };

  return (
    <div
      className="dp-modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16,
        animation: 'dp-modal-overlay 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--dp-bg-glass)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 16,
          padding: 28,
          maxWidth: 480,
          width: '100%',
          border: '1px solid var(--dp-border-medium)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          animation: 'dp-modal-scale 0.25s cubic-bezier(0.4,0,0.2,1)',
          fontFamily: 'var(--dp-font)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              color: 'var(--dp-text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <FileJson size={20} />
            {t('viralNiches.import_json_file')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: '1px solid var(--dp-border-subtle)',
              background: 'transparent',
              color: 'var(--dp-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={16} />
          </button>
        </div>

        <p
          style={{
            margin: '0 0 14px 0',
            fontSize: 13,
            color: 'var(--dp-text-secondary)',
            lineHeight: 1.5,
          }}
        >
          {t('viralNiches.smart_system_desc')}
        </p>

        <button
          type="button"
          className="dp-btn-primary"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          style={{
            width: '100%',
            padding: '12px 16px',
          }}
        >
          {uploading
            ? t('viralNiches.processing')
            : t('viralNiches.select_file')}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
      </div>
    </div>
  );
};

export default NicheFinderUploadModal;
